import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';

interface OnboardingState {
  step: string;
  version: number;
  workspaceType: 'personal' | 'team' | 'enterprise' | null;
  selectedOutcomes: string[];
  isComplete: boolean;
}

/**
 * InviteTeammates page (Step 4 of v2 onboarding - team/enterprise only)
 *
 * Allows users to invite team members via email.
 * Personal workspaces skip this step.
 */
export function InviteTeammatesPage() {
  const [emails, setEmails] = useState<string[]>(['', '', '']);
  const [state, setState] = useState<OnboardingState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  // Load onboarding state
  useEffect(() => {
    async function loadData() {
      try {
        const stateRes = await fetch('/api/onboarding/state', { credentials: 'include' });
        const stateData = await stateRes.json();

        if (stateRes.ok && stateData.state) {
          setState(stateData.state);

          // Redirect personal-typed business workspaces straight to complete.
          if (stateData.state.workspaceType === 'personal') {
            navigate(onboardingPath('business', 'complete', continueUrl));
          }
        }
      } catch {
        setError('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [navigate, continueUrl]);

  const updateEmail = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const addEmailField = () => {
    if (emails.length < 10) {
      setEmails([...emails, '']);
    }
  };

  const removeEmailField = (index: number) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      setEmails(newEmails);
    }
  };

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email.trim() === '' || emailRegex.test(email.trim());
  };

  // Get valid non-empty emails
  const getValidEmails = (): string[] => {
    return emails.filter((e) => e.trim() !== '' && isValidEmail(e)).map((e) => e.trim());
  };

  const handleBack = async () => {
    try {
      await fetch('/api/onboarding/back', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors on back navigation
    }

    navigate(onboardingPath('business', 'review', continueUrl));
  };

  const handleContinue = async (skip: boolean = false) => {
    setIsSaving(true);
    setError('');

    const validEmails = skip ? [] : getValidEmails();

    try {
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          step: 'invites',
          data: { emails: validEmails, skip },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to send invites');
        return;
      }

      navigate(onboardingPath('business', 'complete', continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Check for invalid emails
  const hasInvalidEmails = emails.some((e) => e.trim() !== '' && !isValidEmail(e));
  const validEmailCount = getValidEmails().length;

  if (isLoading) {
    return (
      <OnboardingLayout currentStep="invites" workspaceType={state?.workspaceType} onboardingMode="business">
        <div className="bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout currentStep="invites" workspaceType={state?.workspaceType} onboardingMode="business">
      <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Invite Your Team</h1>
          <p className="text-gray-500 mt-2">
            Get your team on board to make the most of your workspace
          </p>
        </div>

        <div className="max-w-md mx-auto">
          {/* Email inputs */}
          <div className="space-y-3 mb-6">
            {emails.map((email, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => updateEmail(index, e.target.value)}
                    placeholder="colleague@company.com"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      email.trim() !== '' && !isValidEmail(email)
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200'
                    }`}
                  />
                </div>
                {emails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEmailField(index)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Remove email"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add more button */}
          {emails.length < 10 && (
            <button
              type="button"
              onClick={addEmailField}
              className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-gray-300 hover:text-gray-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add another
            </button>
          )}

          {/* Info text */}
          <p className="text-sm text-gray-400 text-center mt-4">
            They'll receive an email invitation to join your workspace
          </p>

          {/* Trial info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <div className="flex gap-3">
              <svg
                className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-sm">
                <p className="font-medium text-blue-900">Trial seats are unlimited</p>
                <p className="text-blue-700 mt-1">
                  Invite as many teammates as you need during your trial period.
                </p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="max-w-md mx-auto mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="max-w-md mx-auto mt-8 pt-6 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="px-6 py-2.5 text-gray-600 hover:text-gray-900 transition-colors font-medium"
          >
            Back
          </button>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => handleContinue(true)}
              disabled={isSaving}
              className="px-6 py-2.5 text-gray-600 hover:text-gray-900 transition-colors font-medium"
            >
              Skip for now
            </button>
            <button
              type="button"
              onClick={() => handleContinue(false)}
              disabled={isSaving || hasInvalidEmails || validEmailCount === 0}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving
                ? 'Sending...'
                : validEmailCount > 0
                ? `Send ${validEmailCount} invite${validEmailCount !== 1 ? 's' : ''}`
                : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
}
